import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CircleCheck,
  Clock,
  DollarSign,
  MapPin,
  Package,
  ShieldAlert,
  Truck,
  Users,
} from "lucide-react";

import { ApiError, apiGet } from "../api";
import { connectConsoleWs } from "../ws";
import { formatCash } from "../cash";
import { formatRecordedMoney, moneyField, projectionAsOf } from "../money";
import type { Board as BoardData, DeliveryCard, DispatchStats, FleetDriver } from "../types";

function clockTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function beep(): void {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    gain.gain.value = 0.05;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.15);
  } catch {
    /* audio not available */
  }
}

const LEGACY_DISPATCH_PROJECTION_KEYS = [
  "contract_version",
  "evidence_state",
  "aggregate_state",
  "aggregate_revision",
  "assignment_generation",
  "operation_state",
  "custody_state",
  "safety_hold",
  "freshness_state",
  "provider_observed_at",
  "history_state",
  "allowed_actions",
] as const;

function hasLegacyDispatchProjection(d: DeliveryCard): boolean {
  const projection: unknown = d.dispatch_saga_projection;
  if (typeof projection !== "object" || projection === null || Array.isArray(projection)) return false;

  try {
    const prototype = Object.getPrototypeOf(projection);
    const record = projection as Record<string, unknown>;
    const keys = Object.keys(record);
    if (
      (prototype !== Object.prototype && prototype !== null) ||
      keys.length !== LEGACY_DISPATCH_PROJECTION_KEYS.length ||
      !LEGACY_DISPATCH_PROJECTION_KEYS.every((key) => Object.prototype.hasOwnProperty.call(record, key))
    ) {
      return false;
    }

    return (
      record.contract_version === "delivery-dispatch-saga.v1" &&
      record.evidence_state === "legacy_unverified" &&
      record.aggregate_state === null &&
      record.aggregate_revision === null &&
      record.assignment_generation === null &&
      record.operation_state === null &&
      record.custody_state === "unknown" &&
      record.safety_hold === "migration_unresolved" &&
      record.freshness_state === "unknown" &&
      record.provider_observed_at === null &&
      record.history_state === "unavailable" &&
      Array.isArray(record.allowed_actions) &&
      record.allowed_actions.length === 0
    );
  } catch {
    return false;
  }
}

function Card({ d }: { d: DeliveryCard }) {
  const assignee = d.driver_name || (d.courier_provider ? `${d.courier_provider} (courier)` : null);
  const eta = clockTime(d.promised_eta);
  const win = clockTime(d.window_start);
  const hasProjection = hasLegacyDispatchProjection(d);
  return (
    <div className="card">
      <div className="card-top">
        <span className="order">{d.order_name || "—"}</span>
        {d.rto_risk_band === "high" && (
          <span className="chip chip-risk" title="High RTO risk">
            <ShieldAlert size={11} /> risk
          </span>
        )}
        {d.payment_type === "cod" && <span className="chip chip-cod" title={`Recorded legacy amount, not collection or custody proof; ${projectionAsOf(d.recorded_money?.as_of)}`}>Recorded COD {formatRecordedMoney(moneyField(d.recorded_money, "cod_amount"))}</span>}
      </div>
      <div className="card-cust">{d.customer_name || "Guest"}</div>
      <div className="card-meta">
        {d.city && (
          <span>
            <MapPin size={11} /> {d.city}
          </span>
        )}
        {(eta || win) && (
          <span>
            <Clock size={11} /> {eta || win}
          </span>
        )}
        <span className="tip" title={projectionAsOf(d.recorded_money?.as_of)}>Recorded tip: {formatRecordedMoney(moneyField(d.recorded_money, "tip_amount"))}</span>
      </div>
      <div className="card-meta">
        <span>{hasProjection ? "Legacy dispatch evidence (unverified)" : "Dispatch verification unavailable"}</span>
        <span>Legacy status (unverified): {d.status}</span>
      </div>
      <div className="card-meta">
        <span>Freshness: unknown</span>
        <span>History: unavailable</span>
        <span>Custody: unknown</span>
      </div>
      <div className="card-foot">
        <span className={`assignee ${assignee ? "" : "unassigned"}`}>
          Legacy assignment (unverified): {assignee || "unassigned"}
        </span>
        <span className="attempts">Legacy attempts (unverified): {d.attempts}</span>
      </div>
      {d.failure_reason && (
        <div className="card-fail">Legacy failure reason (unverified): {d.failure_reason}</div>
      )}
      <div className="card-fail">Dispatch actions unavailable until current authority is verified.</div>
    </div>
  );
}

function Lane({
  title,
  cards,
  tone,
}: {
  title: string;
  cards: DeliveryCard[];
  tone: string;
}) {
  return (
    <section className={`lane lane-${tone}`}>
      <header className="lane-head">
        <span>{title}</span>
        <span className="lane-count">{cards.length}</span>
      </header>
      <div className="lane-body">
        {cards.length === 0 ? (
          <p className="lane-empty">Nothing here</p>
        ) : (
          cards.map((c) => <Card key={c.id} d={c} />)
        )}
      </div>
    </section>
  );
}

function Kpi({ icon, label, value, tone }: { icon: React.ReactNode; label: string; value: string | number; tone?: string }) {
  return (
    <div className={`kpi ${tone ? `kpi-${tone}` : ""}`}>
      <div className="kpi-icon">{icon}</div>
      <div className="kpi-body">
        <div className="kpi-value">{value}</div>
        <div className="kpi-label">{label}</div>
      </div>
    </div>
  );
}

export default function Board() {
  const [board, setBoard] = useState<BoardData | null>(null);
  const [fleet, setFleet] = useState<FleetDriver[] | null>(null);
  const [stats, setStats] = useState<DispatchStats | null>(null);
  const [live, setLive] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const soundReady = useRef(false);

  const load = useCallback(async () => {
    try {
      const [b, f, s] = await Promise.all([
        apiGet<BoardData>("/dispatch/board"),
        apiGet<{ drivers: FleetDriver[] }>("/dispatch/fleet"),
        apiGet<DispatchStats>("/dispatch/stats"),
      ]);
      setBoard(b);
      setFleet(f.drivers);
      setStats(s);
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed to load the board");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const t = setInterval(() => void load(), 15000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const dispose = connectConsoleWs((e) => {
      if (e.event === "system") {
        setLive(true);
      } else if (e.event === "ping") {
        /* keepalive */
      } else {
        void load();
        if (e.sound && soundReady.current) beep();
      }
    });
    return () => {
      dispose();
      setLive(false);
    };
  }, [load]);

  // Browsers block audio until a user gesture — arm the ping after first click.
  useEffect(() => {
    const arm = () => {
      soundReady.current = true;
    };
    window.addEventListener("pointerdown", arm, { once: true });
    return () => window.removeEventListener("pointerdown", arm);
  }, []);

  return (
    <div className="board-page">
      <div className="board-header">
        <h1>Dispatch board</h1>
        <span className={`live ${live ? "on" : "off"}`}>
          <span className="dot" /> {live ? "Updates connected" : "Updates connecting…"}
        </span>
      </div>

      {err && <div className="banner-err">{err}</div>}

      <div className="kpis">
        <Kpi icon={<Package size={18} />} label="Legacy queue count (unverified)" value={stats?.queue ?? "—"} tone="queue" />
        <Kpi icon={<Truck size={18} />} label="Legacy active count (unverified)" value={stats?.active ?? "—"} tone="active" />
        <Kpi icon={<AlertTriangle size={18} />} label="Legacy attention count (unverified)" value={stats?.attention ?? "—"} tone="attention" />
        <Kpi icon={<ShieldAlert size={18} />} label="Legacy RTO-risk count (unverified)" value={stats?.at_risk ?? "—"} tone="risk" />
        <Kpi icon={<Users size={18} />} label="Drivers online" value={stats?.drivers_online ?? "—"} />
        <Kpi icon={<CircleCheck size={18} />} label="Legacy delivered-today count (unverified)" value={stats?.delivered_today ?? "—"} tone="done" />
        <Kpi icon={<DollarSign size={18} />} label="Cash held by riders (custody)"
          value={!stats || stats.rider_cash_by_currency === undefined ? "Unknown"
            : stats.rider_cash_by_currency.length === 0 ? "None"
              : stats.rider_cash_by_currency.map((t) => formatCash(t.outstanding_minor, t.currency_exponent, t.currency)).join(" · ")} />
      </div>

      <div className="board-grid">
        <div className="lanes">
          <Lane title="Queue" tone="queue" cards={board?.lanes.queue ?? []} />
          <Lane title="Active" tone="active" cards={board?.lanes.active ?? []} />
          <Lane title="Attention" tone="attention" cards={board?.lanes.attention ?? []} />
        </div>

        <aside className="fleet">
          <header className="fleet-head">
            <Users size={15} /> Fleet
          </header>
          <div className="fleet-body">
            {(fleet ?? []).length === 0 ? (
              <p className="lane-empty">No drivers online</p>
            ) : (
              (fleet ?? []).map((dr) => (
                <div className="driver" key={dr.id}>
                  <span className={`sdot s-${dr.status}`} title={dr.status} />
                  <div className="driver-main">
                    <span className="driver-name">{dr.name}</span>
                    <span className="driver-sub">
                      {dr.vehicle_type} · {dr.active_deliveries} active
                      {(dr.cash_custody_by_currency ?? []).length > 0
                        && ` · holds ${(dr.cash_custody_by_currency ?? []).map((c) => formatCash(c.outstanding_minor, c.currency_exponent, c.currency)).join(", ")}`}
                    </span>
                  </div>
                  {dr.rating_avg != null && <span className="driver-rating">★ {dr.rating_avg.toFixed(1)}</span>}
                </div>
              ))
            )}
          </div>
        </aside>
      </div>

      <p className="cap-note">
        {board?.truncated ? `Showing the newest ${board.cap} active deliveries. ` : ""}
        <Link to="/work-list">Open the separately paged, oldest-first work list</Link> for the full current queue.
      </p>
    </div>
  );
}
